import { Signal } from "@engine";
import { LEVELS } from "../level/levels";
import type { DifficultyId } from "../config/difficulty";

const STORAGE_KEY = "morph.storyprogress.v1";

// Per-difficulty story progression. The blob stores, for each
// difficulty, the highest unlocked level (1-indexed). Level 1 is
// always unlocked for every difficulty so a fresh player isn't
// staring at three locked grids on first launch.
//
// markCleared(diff, levelN) is called by MorphHexGame.onSuccess after
// a story-mode level is beaten; it bumps unlocked[diff] to N+1
// (capped at LEVELS.length so "unlocked = 6" means all six levels are
// playable). LevelSelectScreen reads getUnlocked / isUnlocked on
// render and re-renders on onChanged so the player sees the new card
// light up the moment they return to the menu.
//
// Per-difficulty scoping (vs global): clearing level 3 on Easy does
// NOT unlock level 4 on Hard. The player has to demonstrate they can
// hold the line on each tier, which gives Hard real teeth as a
// progression mode and not just a multiplier knob.

export interface StoryProgressBlob {
  unlocked: { easy: number; normal: number; hard: number };
}

const EMPTY: StoryProgressBlob = {
  unlocked: { easy: 1, normal: 1, hard: 1 },
};

export class StoryProgressService {
  // Fired whenever any per-difficulty unlocked counter actually
  // changes. LevelSelectScreen subscribes to re-render in place when
  // a new level lights up while the player is staring at the grid
  // (e.g. they entered the screen, came back from a clear).
  readonly onChanged = new Signal<DifficultyId>();

  private blob: StoryProgressBlob;

  constructor() {
    this.blob = this.load();
  }

  // Highest unlocked level for the given difficulty (1-indexed).
  // Returns 1 by default so a missing/corrupt blob still leaves
  // level 1 playable.
  getUnlocked(diff: DifficultyId): number {
    return Math.max(1, Math.min(LEVELS.length, this.blob.unlocked[diff] ?? 1));
  }

  isUnlocked(diff: DifficultyId, level1Indexed: number): boolean {
    return level1Indexed <= this.getUnlocked(diff);
  }

  // Story success path calls this with the level the player just
  // beat. We bump the per-diff counter to N+1 so the next level is
  // playable; capped at LEVELS.length so beating the final level
  // leaves the unlock counter at its terminal value (and isUnlocked
  // returns true for every level).
  markCleared(diff: DifficultyId, level1Indexed: number): void {
    const target = Math.min(LEVELS.length, level1Indexed + 1);
    const current = this.blob.unlocked[diff] ?? 1;
    if (target <= current) return;
    this.blob.unlocked[diff] = target;
    this.persist();
    this.onChanged.emit(diff);
  }

  // Debug / "wipe progress" hook. Resets every difficulty back to
  // level-1 only and clears the persisted blob.
  clear(): void {
    this.blob = {
      unlocked: { easy: 1, normal: 1, hard: 1 },
    };
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore quota / privacy errors
    }
  }

  private load(): StoryProgressBlob {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return { unlocked: { ...EMPTY.unlocked } };
      const parsed = JSON.parse(raw) as Partial<StoryProgressBlob> | null;
      if (!parsed || typeof parsed !== "object" || !parsed.unlocked) {
        return { unlocked: { ...EMPTY.unlocked } };
      }
      // Defensive merge so a future schema bump doesn't crash on first
      // load -- missing per-diff values fall back to 1.
      const u = parsed.unlocked as Partial<StoryProgressBlob["unlocked"]>;
      return {
        unlocked: {
          easy: this.sanitize(u.easy),
          normal: this.sanitize(u.normal),
          hard: this.sanitize(u.hard),
        },
      };
    } catch {
      return { unlocked: { ...EMPTY.unlocked } };
    }
  }

  private sanitize(v: unknown): number {
    if (typeof v !== "number" || !Number.isFinite(v)) return 1;
    return Math.max(1, Math.min(LEVELS.length, Math.floor(v)));
  }

  private persist(): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.blob));
    } catch {
      // private mode / quota errors -- ignore
    }
  }
}
